import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { MessageService } from '@corelabs/angular/messages';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concat, EMPTY, exhaustMap, filter, from, interval, map, of, switchMap, tap, timeout } from 'rxjs';
import { build } from '@build';
import { environment } from '@env/environment';
import { updateActions } from '../actions/update.actions';
import { selectBundleId, selectStatus, selectVersion } from '../selectors/update.selector';
import { HttpService } from '../services/http.service';

const CHECK_INTERVAL = 2 * 60 * 60 * 1000;

// exhaustMap holds the whole feature open until this inner chain settles, and neither half of it
// settles on its own: CapacitorHttp applies no request timeout, and CapacitorUpdater.download()
// waits on a WorkManager job that never completes while its network constraint is unmet.
const CHECK_TIMEOUT = 2 * 60 * 1000;

@Injectable()
export class UpdateEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    // Safety net, not the cadence — shell entry and unlock are the real triggers. Bounds
    // staleness on a device that stays foregrounded and never locks.
    checkPeriodically$ = createEffect(() => {
        return interval(CHECK_INTERVAL).pipe(map(() => updateActions.check()));
    });

    check$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(updateActions.check),
            filter(() => Capacitor.isNativePlatform() && !!environment.updateUrl),
            concatLatestFrom(() => [this.store.select(selectStatus), this.store.select(selectBundleId), this.store.select(selectVersion)]),
            // 'pending' still checks: a newer build supersedes the queued bundle.
            filter(([, status]) => status !== 'downloading'),
            // exhaustMap, not switchMap: a check firing mid-download must not cancel and
            // restart the zip transfer.
            exhaustMap(([, , pendingId, pendingVersion]) => {
                return this.httpService.getManifest().pipe(
                    timeout(CHECK_TIMEOUT),
                    // A missing latest.json resolves with the host's error body (or an
                    // index.html fallback) — CapacitorHttp does not reject on non-2xx.
                    filter((manifest) => !!manifest.url && manifest.version !== build.version && manifest.version !== pendingVersion),
                    switchMap((manifest) => {
                        return concat(
                            of(updateActions.downloading()),
                            // No next(): that arms the plugin's own apply-on-background, which
                            // reloads past the lock gate. The bridge owns the apply instead.
                            from(CapacitorUpdater.download(manifest)).pipe(
                                timeout(CHECK_TIMEOUT),
                                tap(() => {
                                    if (pendingId) {
                                        void CapacitorUpdater.delete({ id: pendingId });
                                    }
                                }),
                                map((bundle) => updateActions.downloaded({ payload: { id: bundle.id, version: bundle.version } })),
                                catchError(() => of(updateActions.failed())),
                            ),
                        );
                    }),
                    // An unreachable host is the normal state of a device carried out of Wi-Fi
                    // range — silent, and retried on the next unlock.
                    catchError(() => EMPTY),
                );
            }),
        );
    });

    // Only a download that started and broke reaches 'failed' — a check that never got the
    // manifest ends silently.
    failed$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(updateActions.failed),
                tap(() => this.messageService.add(this.translateService.instant('update.error'), 'danger')),
            );
        },
        { dispatch: false },
    );

    // Manual apply from the profile page: an operator is present and chose to reload now, so no
    // relock flag — set() reloads the app onto the bundle and it boots straight into the session.
    apply$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(updateActions.apply),
                concatLatestFrom(() => this.store.select(selectBundleId)),
                filter(([, bundleId]) => !!bundleId),
                tap(([, bundleId]) => void CapacitorUpdater.set({ id: bundleId! })),
            );
        },
        { dispatch: false },
    );
}
